import {
  FOC_TOUR_LABEL_INCIDENT_ROSTER,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import {
  FOC_REQUIRED_DISPOSITION,
  IOC_CARRYOVER_DISPOSITION,
} from '../orgWorkspaceTour/orgWorkspaceTourSteps';
import { CorporateViewTabs } from '../hubNavTabs';
import { preferredFocIncidentRosterId } from './focIncidentRosterDemo';

export const FOC_INCIDENT_ROSTER_TOUR_PARAM = 'focIncidentRosterTour';

/** Opens Hub Incidents; host resolves a live incident and navigates to roster. */
export const FOC_INCIDENT_ROSTER_TOUR_HREF = `/hub?openTab=${CorporateViewTabs.Incidents}&${FOC_INCIDENT_ROSTER_TOUR_PARAM}=1`;

export type FocIncidentRosterTourSurface = 'hub' | 'roster' | 'settings';

export type FocIncidentRosterTourStepId =
  | 'sectionIntro'
  | 'usir001'
  | 'usir002'
  | 'usir004'
  | 'usir005'
  | 'usir003'
  | 'usir007'
  | 'usir008'
  | 'usir010'
  | 'usir011'
  | 'usir013'
  | 'usir015'
  | 'usir018'
  | 'usir021'
  | 'usir023'
  | 'usir024'
  | 'usir025'
  | 'usir026'
  | 'inc006'
  | 'inc006a'
  | 'inc006b'
  | 'inc006c'
  | 'inc006d'
  | 'focFeedback';

export type FocIncidentRosterTourStepDef = {
  id: FocIncidentRosterTourStepId;
  requirementId?: string;
  title: string;
  disposition?: string;
  description: string;
  bullets?: string[];
  tour?: string;
  surface?: FocIncidentRosterTourSurface;
};

type RequirementInput = Omit<
  FocIncidentRosterTourStepDef,
  'title' | 'requirementId'
> & {
  requirementId: string;
  name: string;
};

function requirement({
  requirementId,
  name,
  ...rest
}: RequirementInput): FocIncidentRosterTourStepDef {
  return {
    ...rest,
    requirementId,
    title: `${requirementId} — ${name}`,
  };
}

/**
 * Walk for 7.2 Incident Roster: US-IR* (carryover) and FOC-INC-006*,
 * ending with FOC Feedback Notes.
 */
export const FOC_INCIDENT_ROSTER_TOUR_STEPS: FocIncidentRosterTourStepDef[] = [
  {
    id: 'sectionIntro',
    surface: 'roster',
    tour: 'roster-rapid-dev',
    title: '7.2 Incident Roster',
    description:
      'Manage who is assigned to the incident response from the Incident Roster — list, status, search, add/edit/remove, Teams collaboration, and rapid ICS 201 roster development.',
  },
  requirement({
    id: 'usir001',
    requirementId: 'US-IR001',
    name: 'View Incident Roster',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    tour: 'incident-roster-table',
    description:
      'Switch to Table View to see all incident roster members in a list format so you can review who is assigned to the incident response.',
  }),
  requirement({
    id: 'usir002',
    requirementId: 'US-IR002',
    name: 'View Roster Status',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    tour: 'roster-member-summary',
    description:
      'In the collapsed row, see each member’s identity, position/seats, activation status, check-in status, and sign-in status so you can assess roster status at a glance.',
  }),
  requirement({
    id: 'usir004',
    requirementId: 'US-IR004',
    name: 'View Activation Status',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    tour: 'roster-activation-status',
    description:
      'Activation status uses color-coded text today: green Activated, amber Pending, and red Declined (with timestamps when available). Dedicated green/amber/gray dots and “Awaiting Confirmation / Not Activated” labels continue to mature.',
  }),
  requirement({
    id: 'usir005',
    requirementId: 'US-IR005',
    name: 'View Check-In and Sign-In Status',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    tour: 'roster-checkin-status',
    description:
      'Check-in and daily sign-in statuses use color-coded text (for example Checked In / Signed In in green) so you can quickly assess personnel availability.',
  }),
  requirement({
    id: 'usir003',
    requirementId: 'US-IR003',
    name: 'View Roster Member Details',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    tour: 'roster-member-detail',
    description:
      'Expand an individual roster member to view detailed contact information, seats, activation controls, and check-in / sign-in actions.',
  }),
  requirement({
    id: 'usir007',
    requirementId: 'US-IR007',
    name: 'Search Roster Members',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    tour: 'roster-search',
    description:
      'Search roster members by name, email, or position title from the search field so you can quickly find specific personnel.',
  }),
  requirement({
    id: 'usir008',
    requirementId: 'US-IR008',
    name: 'Persistent Roster Search',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    tour: 'roster-search-bar',
    description:
      'The search bar stays pinned at the top of the Table View so you can filter roster members at any time without scrolling.',
  }),
  requirement({
    id: 'usir010',
    requirementId: 'US-IR010',
    name: 'Required Roster Information',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    tour: 'roster-add-member',
    description:
      'Use + Add when adding personnel. Required contact details such as name and email keep the roster accurate; position is chosen when you assign into a seat or add a position.',
  }),
  requirement({
    id: 'usir011',
    requirementId: 'US-IR011',
    name: 'Select Position from Predefined List',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    tour: 'roster-add-position',
    description:
      'Select a position from the predefined incident seat list when adding or placing a member so personnel map to organizational roles.',
  }),
  requirement({
    id: 'usir013',
    requirementId: 'US-IR013',
    name: 'Set Activation Status',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    tour: 'roster-activation-status',
    description:
      'Activation state (Activated, Pending, or Declined) is tracked on roster members. Setting activation during the initial add flow continues to mature; after add, expand a member to confirm or decline activation.',
  }),
  requirement({
    id: 'usir015',
    requirementId: 'US-IR015',
    name: 'Roster Addition Confirmation',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    description:
      'When a member is successfully added, PRATUS shows a success notification confirming the action completed. This walkthrough does not mutate the roster.',
  }),
  requirement({
    id: 'usir018',
    requirementId: 'US-IR018',
    name: 'Change Member Position',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    tour: 'roster-edit-member-dialog',
    description:
      'Edit a member’s seats to reassign personnel to different roles from the predefined position list.',
  }),
  requirement({
    id: 'usir021',
    requirementId: 'US-IR021',
    name: 'Roster Update Confirmation',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    description:
      'When a member is successfully updated, PRATUS shows a success notification confirming the changes were saved. This walkthrough does not mutate the roster.',
  }),
  requirement({
    id: 'usir023',
    requirementId: 'US-IR023',
    name: 'Remove Roster Member',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    tour: 'roster-remove-member',
    description:
      'Remove a member from the roster (Remove Member) when they are no longer assigned to the incident response.',
  }),
  requirement({
    id: 'usir024',
    requirementId: 'US-IR024',
    name: 'Roster Removal Confirmation',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    description:
      'When a member is successfully removed, PRATUS shows a success notification confirming the action completed. This walkthrough does not mutate the roster.',
  }),
  requirement({
    id: 'usir025',
    requirementId: 'US-IR025',
    name: 'Generate Microsoft Teams Site',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'settings',
    tour: 'incident-generate-teams',
    description:
      'From Incident Settings, generate a Microsoft Teams site for the incident so the roster has a collaboration workspace. The Generate Teams Site control lives on Settings rather than the Roster page today.',
  }),
  requirement({
    id: 'usir026',
    requirementId: 'US-IR026',
    name: 'Synchronize Resource and Roster Assignment',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'roster',
    tour: 'incident-roster-table',
    description:
      'When personnel are assigned to a resource already linked to a roster seat, PRATUS can synchronize that assignment onto the Incident Roster with the relevant position. There is no separate sync button on the roster table; synchronization happens through the shared assignment model.',
  }),
  requirement({
    id: 'inc006',
    requirementId: 'FOC-INC-006',
    name: 'Rapid Initial Roster Development',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'roster',
    tour: 'incident-roster-workspace',
    description:
      'PRATUS supports rapid initial roster development during the ICS 201 phase. Authorized users can add personnel, identify acting or temporary positions, assign initial roles, and later refine assignments as the incident organization matures.',
  }),
  requirement({
    id: 'inc006a',
    requirementId: 'FOC-INC-006a',
    name: 'IMH-Based Position Defaults',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'roster',
    tour: 'foc-roster-load',
    description:
      'PRATUS presents Incident Management Handbook–aligned positions and likely subordinate positions as available defaults (for example via roster templates) while allowing Coast Guard-specific, specialty, local, Unified Command, and incident-specific positions.',
  }),
  requirement({
    id: 'inc006b',
    requirementId: 'FOC-INC-006b',
    name: 'Roster and Organization Synchronization',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'roster',
    tour: 'incident-roster-workspace',
    description:
      'PRATUS uses a common underlying incident-organization data model for incident roster, organization chart, ICS 203, ICS 207, resources, and related assignment products so updates stay aligned across those surfaces.',
  }),
  requirement({
    id: 'inc006c',
    requirementId: 'FOC-INC-006c',
    name: 'External Personnel',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'roster',
    tour: 'roster-add-external',
    description:
      'Authorized external-agency, partner, Responsible Party, contractor, volunteer, and stakeholder personnel can be entered on the roster (Assign New External User) without automatically granting platform access or Coast Guard resource-request authority.',
  }),
  requirement({
    id: 'inc006d',
    requirementId: 'FOC-INC-006d',
    name: 'Persistent External Personnel Within Organizational Roster',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'roster',
    tour: 'roster-external-persistent',
    description:
      'PRATUS supports pre-loading authorized external personnel into organizational and incident roster structures so those parties can be added quickly into an Incident Workspace when response begins.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_INCIDENT_ROSTER),
  },
];

export function focIncidentRosterTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function focIncidentRosterHref(incidentId: string): string {
  return `/iap/${incidentId}/roster?${FOC_INCIDENT_ROSTER_TOUR_PARAM}=1`;
}

export function focIncidentRosterSettingsHref(incidentId: string): string {
  return `/iap/${incidentId}/settings?${FOC_INCIDENT_ROSTER_TOUR_PARAM}=1`;
}

/** Prefer FOC demo, then current workspace, else first non-archived incident. */
export function resolveFocIncidentRosterHref(
  incidents: readonly {
    id: string;
    name?: string | null;
    archived?: number | boolean;
  }[] = [],
  currentId?: string | null
): string | null {
  const id = preferredFocIncidentRosterId(incidents, currentId);
  return id ? focIncidentRosterHref(id) : null;
}

export function resolveFocIncidentRosterSettingsHref(
  incidents: readonly {
    id: string;
    name?: string | null;
    archived?: number | boolean;
  }[] = [],
  incidentId?: string | null
): string | null {
  const id = preferredFocIncidentRosterId(incidents, incidentId);
  return id ? focIncidentRosterSettingsHref(id) : null;
}

export function focIncidentRosterTourStepIds(): FocIncidentRosterTourStepId[] {
  return FOC_INCIDENT_ROSTER_TOUR_STEPS.map((step) => step.id);
}

export function focIncidentRosterTourStepTitles(): string[] {
  return FOC_INCIDENT_ROSTER_TOUR_STEPS.map((step) => step.title);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function focIncidentRosterTourPopoverHtml(
  step: Pick<
    FocIncidentRosterTourStepDef,
    'disposition' | 'description' | 'bullets'
  >
): string {
  const body = `<p>${escapeHtml(step.description)}</p>`;
  const bullets = step.bullets?.length
    ? `<ul>${step.bullets
        .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
        .join('')}</ul>`
    : '';
  const content = `${body}${bullets}`;
  if (!step.disposition) return content;
  return `<p><strong>Disposition:</strong> ${escapeHtml(step.disposition)}</p>${content}`;
}
